import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PlaceholderList } from './components/PlaceholderList';
import { SaveTemplate } from './components/SaveTemplate';
import { TemplateInfo } from './components/TemplateInfo';
import { PreviewPanel } from './components/PreviewPanel';
import { CreatePlaceholder } from './components/CreatePlaceholder';
import { RepeatingPlaceholders } from './components/RepeatingPlaceholders';
import { OpenTemplate } from './components/OpenTemplate';
import { PublishTemplate } from './components/PublishTemplate';
import { ImportExport, type PendingCreateImport } from './components/ImportExport';
import { HelpPanel } from './components/HelpPanel';
import { FragmentPanel } from './components/FragmentPanel';
import { ArtemisCheck } from './components/ArtemisCheck';
import { ArtemisGeneration } from './components/ArtemisGeneration';
import { LoginScreen } from './components/LoginScreen';
import { Can } from './components/Can';
import { ConfirmDialog } from './components/ConfirmDialog';
import { BindingBanner } from './components/BindingBanner';
import { loadDocumentFromBase64, setDocumentProperty, enableAutoShowTaskpane } from './services/wordHelper';
import { auth, getAuthHeaders, API_URL } from './services/auth';
import './App.css';

export type ViewId =
  | 'placeholders'
  | 'create-new'
  | 'repeating'
  | 'info'
  | 'open-template'
  | 'save'
  | 'publish'
  | 'preview'
  | 'import'
  | 'export'
  | 'fragments'
  | 'artemis-check'
  | 'artemis-generation'
  | 'help';

const VIEW_LABELS: Record<ViewId, string> = {
  'placeholders': 'Placeholders',
  'create-new': 'Create & Register',
  'repeating': 'Repeating Sections',
  'info': 'Template Options',
  'open-template': 'Templates',
  'save': 'Save Template',
  'publish': 'Publish Template',
  'preview': 'Composition Preview',
  'import': 'Import / Export',
  'export': 'Import / Export',
  'fragments': 'Fragments',
  'artemis-check': 'Artemis Check',
  'artemis-generation': 'Artemis Generation',
  'help': 'Help',
};

export interface TemplateContext {
  id: number;
  name: string;
  versionNumber: number;
  versionStatus: 'Draft' | 'PendingApproval' | 'Published' | 'Superseded' | 'Rejected';
  isLocked: boolean;
  // Cross-environment stable identity. Optional only for backward compatibility
  // with bindings that pre-date the PublicId migration; new bindings should
  // always populate it so verify-binding can resolve correctly across envs.
  publicId?: string;
}

interface AppProps {
  initialView?: ViewId;
}

export type CreateMode = 'placeholder' | 'keyword' | 'advanced-condition' | 'document-type' | null;

/** Auth gate — keeps the main App's hooks unmounted until the user is logged in. */
export function App(props: AppProps) {
  const [, setAuthVersion] = useState(0);
  useEffect(() => {
    const handler = () => setAuthVersion((v) => v + 1);
    window.addEventListener('docplugin:auth-changed', handler);
    return () => window.removeEventListener('docplugin:auth-changed', handler);
  }, []);
  if (!auth.isAuthenticated()) {
    return <LoginScreen onLoggedIn={() => setAuthVersion((v) => v + 1)} />;
  }
  return <AppInner {...props} />;
}

function AppInner({ initialView = 'placeholders' }: AppProps) {
  const [currentView, setCurrentView] = useState<ViewId>(initialView);
  const label = VIEW_LABELS[currentView] || 'Placeholders';
  const [createMode, setCreateMode] = useState<CreateMode>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState<TemplateContext | null>(null);
  // When set, the Save panel opens in "save as new" mode (strip binding,
  // require user to pick a fresh name + DocumentType). Cleared on save or
  // when the user navigates away.
  const [saveAsNewMode, setSaveAsNewMode] = useState(false);
  // Prefill carried over from a .dpt import that resolved to a new template
  // (Branch C). SaveTemplate consumes this: prefills the name, ensures the
  // imported source PublicId is forwarded on the eventual create POST so
  // cross-env identity is preserved. null when there's no import handoff
  // in progress.
  const [pendingImport, setPendingImport] = useState<PendingCreateImport | null>(null);
  const [savingInBackground, setSavingInBackground] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const bgSaveTriggered = useRef(false);

  // Load active template from Word document properties on mount.
  //
  // The Word.run is async and can resolve LATER than other code paths that
  // also call setActiveTemplate (Save → onSaved → persistTemplate(v11),
  // Edit → persistTemplate, Import → onImportCommitted, etc.). Without
  // care, this late resolve would clobber a freshly-set activeTemplate
  // with the stale dp_* values read from the doc — that's the exact
  // "taskpane stuck on v6 after saving v11" race we hit.
  //
  // Use the functional setActiveTemplate(prev => ...) form to seed ONLY
  // when nothing else has claimed activeTemplate yet. If a save / edit
  // / import beat us, prev is non-null and we leave it untouched. Also
  // try to read dp_publicId so the initial banner can resolve to 'match'
  // for templates whose .docx already carries it (e.g. desktop-opened
  // files that were saved post-PublicId migration).
  useEffect(() => {
    Word.run(async (context) => {
      const props = context.document.properties.customProperties;
      props.load('items');
      await context.sync();
      const get = (k: string) => {
        const p = props.items.find((it: any) => it.key === k);
        return p?.value != null ? String(p.value) : undefined;
      };
      const idStr = get('dp_templateId');
      const nameVal = get('dp_templateName');
      if (idStr && nameVal) {
        const seeded: TemplateContext = {
          id: parseInt(idStr, 10),
          name: nameVal,
          versionNumber: get('dp_versionNumber') ? parseInt(get('dp_versionNumber')!, 10) : 1,
          versionStatus: (get('dp_versionStatus') as TemplateContext['versionStatus']) || 'Draft',
          isLocked: false,
          publicId: get('dp_publicId'),
        };
        setActiveTemplate((prev) => prev ?? seeded);
      }
    }).catch(() => { /* no saved template */ });
  }, []);

  // Background auto-save: when ribbon triggers ?view=save and template exists
  // Clear the URL param immediately to prevent re-trigger on taskpane reload
  useEffect(() => {
    if (initialView === 'save' && activeTemplate && !bgSaveTriggered.current) {
      bgSaveTriggered.current = true;
      // Remove ?view=save from URL so a taskpane reload won't re-trigger
      try { window.history.replaceState({}, '', window.location.pathname + '?view=placeholders'); } catch {}
      setCurrentView('placeholders');
      backgroundSave(activeTemplate);
    }
  }, [activeTemplate, initialView]);

  // Background auto-publish: when ribbon triggers ?view=publish and template exists
  useEffect(() => {
    if (initialView === 'publish' && activeTemplate && !bgSaveTriggered.current) {
      bgSaveTriggered.current = true;
      try { window.history.replaceState({}, '', window.location.pathname + '?view=placeholders'); } catch {}
      setCurrentView('placeholders');
      backgroundSaveAndPublish(activeTemplate);
    }
  }, [activeTemplate, initialView]);

  const checkDocumentType = async (): Promise<boolean> => {
    try {
      const hasDocType = await Word.run(async (context) => {
        const props = context.document.properties.customProperties;
        props.load('items');
        await context.sync();
        const docType = props.items.find((p: any) => p.key === 'dp_docType');
        return !!(docType?.value);
      });
      return hasDocType;
    } catch {
      return false;
    }
  };

  /**
   * Shared post-save refresh. Called by BOTH save paths:
   *   • backgroundSave / backgroundSaveAndPublish (the ribbon-triggered
   *     auto-save when activeTemplate is already set)
   *   • handleTemplateSaved (the SaveTemplate-component manual save)
   *
   * Strategy: fetch the server-authoritative latest version + publicId,
   * write them into the doc's dp_*, then hard-reload the taskpane. The
   * fresh mount's initial doc-read seeds activeTemplate correctly and
   * the binding banner + Template section both reflect reality. Avoids
   * the entire class of "save succeeded but UI didn't update" bugs.
   */
  const refreshTaskpaneAfterSave = async (templateId: number, fallbackName: string) => {
    let final: {
      id: number;
      name: string;
      versionNumber: number;
      versionStatus: string;
      publicId?: string;
    } | null = null;
    try {
      const resp = await fetch(`${API_URL}/templates/${templateId}`, {
        headers: { ...getAuthHeaders() },
      });
      if (resp.ok) {
        const json = await resp.json();
        const t = json?.data;
        if (t) {
          const versions: any[] = Array.isArray(t.versions) ? t.versions : [];
          const live = versions.filter((v) => v.VersionStatus !== 'Deleted');
          let latest = live[0];
          for (const v of live) {
            if ((v.VersionNumber ?? 0) > (latest?.VersionNumber ?? 0)) latest = v;
          }
          if (latest) {
            final = {
              id: t.id ?? t.Id,
              name: t.Name ?? t.name ?? fallbackName,
              versionNumber: latest.VersionNumber,
              versionStatus: latest.VersionStatus || 'Draft',
              publicId: t.PublicId ?? t.publicId,
            };
          }
        }
      }
    } catch { /* fall through to reload-only path */ }

    if (final) {
      try {
        await Word.run(async (ctx) => {
          const props = ctx.document.properties.customProperties;
          props.add('dp_templateId', String(final!.id));
          props.add('dp_templateName', final!.name);
          props.add('dp_versionNumber', String(final!.versionNumber));
          props.add('dp_versionStatus', final!.versionStatus);
          if (final!.publicId) props.add('dp_publicId', final!.publicId);
          await ctx.sync();
        });
      } catch { /* still reload */ }
    }

    // Strip ?view=save / ?view=publish so the reloaded pane doesn't
    // re-trigger an auto-save effect → infinite loop.
    try {
      window.history.replaceState({}, '', window.location.pathname + '?view=placeholders');
    } catch { /* ignore */ }
    // Hard reload — re-mounts the entire app; initial dp_* read seeds
    // activeTemplate from the just-written values.
    window.location.reload();
  };

  const bgSaveLock = useRef(false);
  const backgroundSave = async (template: TemplateContext) => {
    // Prevent double save: check both ref and localStorage timestamp
    const lastSaveTime = Number(localStorage.getItem('dp_lastSaveTime') || '0');
    if (bgSaveLock.current || (Date.now() - lastSaveTime < 10000)) return;
    bgSaveLock.current = true;
    localStorage.setItem('dp_lastSaveTime', String(Date.now()));
    setSavingInBackground(true);
    setSaveMessage(null);

    // Check document type is selected
    const hasDocType = await checkDocumentType();
    if (!hasDocType) {
      bgSaveLock.current = false;
      setSavingInBackground(false);
      setSaveMessage({ type: 'error', text: 'Please select a Document Type before saving. Go to Placeholders → Document Type section.' });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    try {
      await Word.run(async (context) => { context.document.save(); await context.sync(); }).catch(() => {});
      await new Promise(r => setTimeout(r, 500));

      const { getDocumentAsBlob } = await import('./services/wordHelper');
      const blob = await getDocumentAsBlob();
      const formData = new FormData();
      formData.append('file', blob, `${template.name}.docx`);
      formData.append('changeNotes', 'Saved from Word Add-in');

      const response = await fetch(
        `${API_URL}/doc-service/templates/${template.id}/versions`,
        { method: 'POST', headers: getAuthHeaders(), body: formData }
      );
      const data = await response.json();

      if (data.success) {
        const phCount = data.data?.placeholders?.length || 0;
        const newVer = data.data?.versionNumber;
        setSaveMessage({
          type: 'success',
          text: data.data?.duplicate
            ? `Already at v${newVer ?? '?'} — refreshing taskpane…`
            : `Saved v${newVer ?? ''}! ${phCount} placeholder${phCount !== 1 ? 's' : ''} extracted. Refreshing taskpane…`,
        });
        // Refresh-and-reload: fetch the server-authoritative latest, write
        // dp_* into the doc, then full-reload the pane. This is what makes
        // the taskpane actually reflect the new version after save —
        // without it the success message would show but the binding
        // banner + Template section would stay on the pre-save version.
        bgSaveLock.current = false;
        setSavingInBackground(false);
        await refreshTaskpaneAfterSave(template.id, template.name);
        return; // page is reloading; nothing else to do
      } else {
        setSaveMessage({ type: 'error', text: data.error?.message || 'Save failed' });
      }
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err?.message || 'Save failed' });
    } finally {
      bgSaveLock.current = false;
      setSavingInBackground(false);
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const backgroundSaveAndPublish = async (template: TemplateContext) => {
    const lastPubTime = Number(localStorage.getItem('dp_lastPublishTime') || '0');
    if (bgSaveLock.current || (Date.now() - lastPubTime < 10000)) return;
    bgSaveLock.current = true;
    localStorage.setItem('dp_lastPublishTime', String(Date.now()));
    setSavingInBackground(true);
    setSaveMessage(null);

    // Check document type is selected
    const hasDocType = await checkDocumentType();
    if (!hasDocType) {
      bgSaveLock.current = false;
      setSavingInBackground(false);
      setSaveMessage({ type: 'error', text: 'Please select a Document Type before publishing. Go to Placeholders → Document Type section.' });
      setTimeout(() => setSaveMessage(null), 5000);
      return;
    }

    try {
      // Step 1: Save
      setSaveMessage({ type: 'success', text: 'Saving template...' });
      await Word.run(async (context) => { context.document.save(); await context.sync(); }).catch(() => {});
      await new Promise(r => setTimeout(r, 500));

      const { getDocumentAsBlob } = await import('./services/wordHelper');
      const blob = await getDocumentAsBlob();
      const formData = new FormData();
      formData.append('file', blob, `${template.name}.docx`);
      formData.append('changeNotes', 'Published from Word Add-in');

      const saveResp = await fetch(
        `${API_URL}/doc-service/templates/${template.id}/versions`,
        { method: 'POST', headers: getAuthHeaders(), body: formData }
      );
      const saveData = await saveResp.json();

      if (!saveData.success) {
        setSaveMessage({ type: 'error', text: saveData.error?.message || 'Save failed — publish cancelled' });
        return;
      }

      // Step 2: Artemis Check — blocks publish if errors found
      setSaveMessage({ type: 'success', text: 'Running Artemis Check...' });
      const versionNumber = saveData.data?.versionNumber || template.versionNumber;
      try {
        // Extract paragraph-by-paragraph so line breaks survive. body.text
        // concatenates paragraphs with no separator which makes adjacent
        // lines merge (e.g. heading + next line → "BONDBOND NO.") and Claude
        // flags bogus "duplicate word" errors that block publish.
        let documentText = '';
        try {
          await Word.run(async (context) => {
            const paragraphs = context.document.body.paragraphs;
            paragraphs.load('text');
            await context.sync();
            documentText = paragraphs.items.map(p => p.text).join('\n');
          });
        } catch { /* ignore */ }

        // Merged tag scan — includes floating-shape placeholders as well as
        // content controls, so the quality check sees every placeholder.
        let placeholderTags: string[] = [];
        try {
          const { getAllPlaceholderTags } = await import('./services/wordHelper');
          const tags = await getAllPlaceholderTags();
          placeholderTags = [...new Set(tags.map(t => t.tag))];
        } catch { /* ignore */ }

        const qcResp = await fetch(`${API_URL}/ai/quality-check`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({ templateId: template.id, documentText, placeholderTags }),
        });
        const qcData = await qcResp.json();
        if (qcData.success && qcData.data) {
          const qc = qcData.data;
          const errors = qc.issues?.filter((i: any) => i.severity === 'error') || [];
          const warnings = qc.issues?.filter((i: any) => i.severity === 'warning') || [];

          if (errors.length > 0) {
            // BLOCK publish — show Artemis Check with results
            setSaveMessage({
              type: 'error',
              text: `Artemis Check failed (${errors.length} error${errors.length > 1 ? 's' : ''}). Fix errors before publishing.`,
            });
            // Switch to Artemis Check view to show full results
            setCurrentView('artemis-check');
            return;
          }

          if (warnings.length > 0) {
            setSaveMessage({
              type: 'success',
              text: `Artemis: ${qc.score}/100 — ${warnings.length} warning(s). Publishing...`,
            });
            await new Promise(r => setTimeout(r, 1500));
          } else {
            setSaveMessage({ type: 'success', text: `Artemis: ${qc.score}/100 ✓ Publishing...` });
          }
        }
      } catch {
        // Quality check failure shouldn't block publish
        setSaveMessage({ type: 'success', text: 'Artemis Check unavailable — publishing...' });
      }

      // Step 3: Publish (only reaches here if no errors)
      setSaveMessage({ type: 'success', text: 'Publishing template...' });

      const publishResp = await fetch(
        `${API_URL}/templates/${template.id}/versions/${versionNumber}/publish`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        }
      );
      const publishData = await publishResp.json();

      if (publishData.success) {
        if (publishData.data?.approvalRequired) {
          setSaveMessage({ type: 'success', text: 'Submitted for approval. You will be notified when approved.' });
        } else {
          setSaveMessage({ type: 'success', text: `Published! v${versionNumber} is now live.` });
          persistTemplate({ ...template, versionStatus: 'Published', versionNumber });
        }
      } else {
        setSaveMessage({ type: 'error', text: publishData.error?.message || 'Publish failed' });
      }
    } catch (err: any) {
      setSaveMessage({ type: 'error', text: err?.message || 'Save & publish failed' });
    } finally {
      bgSaveLock.current = false;
      setSavingInBackground(false);
      setTimeout(() => setSaveMessage(null), 5000);
    }
  };

  // Persist active template to Word document properties. dp_publicId is the
  // identifier verify-binding actually trusts — dp_templateId is kept for
  // back-compat readability but is not the source of truth across envs.
  const persistTemplate = (template: TemplateContext | null) => {
    setActiveTemplate(template);
    if (template) {
      Word.run(async (context) => {
        const props = context.document.properties.customProperties;
        props.add('dp_templateId', String(template.id));
        props.add('dp_templateName', template.name);
        props.add('dp_versionNumber', String(template.versionNumber));
        props.add('dp_versionStatus', template.versionStatus);
        if (template.publicId) {
          props.add('dp_publicId', template.publicId);
        }
        await context.sync();
      }).catch(() => {});
      // Flag the doc so the taskpane auto-opens on every future open —
      // including desktop-open, where the binding banner would otherwise
      // never appear. Fire-and-forget: a save must not fail over this.
      enableAutoShowTaskpane().catch(() => {});
    }
  };

  const handleNewOption = (mode: CreateMode) => {
    setDropdownOpen(false);
    setCreateMode(prev => prev === mode ? null : mode);
  };

  const handleTemplateSaved = async (template: TemplateContext) => {
    setSaveAsNewMode(false);
    setPendingImport(null);
    // Optimistic update — brief glimpse before the reload completes.
    persistTemplate(template);
    // Delegate the heavy lifting to the shared helper so this path and the
    // ribbon-triggered backgroundSave path behave identically.
    await refreshTaskpaneAfterSave(template.id, template.name);
  };

  // Edit / Open / Revert path — the user has just LOADED an existing
  // version into Word. This is NOT a save and must NOT trigger the
  // reload-after-save flow: that flow re-fetches the server's latest
  // version and stamps it into dp_*, which silently mis-labels the
  // just-loaded older version as "latest" in the taskpane. Just persist
  // the binding and navigate; the document body and dp_* were already
  // written correctly by the loader (OpenTemplate / TemplateInfo).
  const handleTemplateLoaded = (template: TemplateContext) => {
    persistTemplate(template);
    setCurrentView('placeholders');
  };

  // BindingBanner callbacks. Each one represents a user-chosen resolution
  // for a non-trivial binding state surfaced by verify-binding:
  //
  //   onRebind        — file's PublicId resolves to a different local Id
  //                     than what's stamped in dp_templateId. We update the
  //                     active binding and re-stamp dp_* so future saves
  //                     target the correct row. The old dp_templateId is
  //                     silently overwritten; no data loss because the
  //                     PublicId is the authoritative identity.
  //   onSaveAsNew     — orphan/rebind path where the user wants to fork:
  //                     route to the Save panel in save-as-new mode (the
  //                     panel will strip the binding and ask for a name +
  //                     DocumentType).
  //   onReloadLatest  — drift==='behind'; pull the latest version into the
  //                     doc so editing is on top of current state. Delegates
  //                     to OpenTemplate by switching the view.
  const handleRebind = (next: TemplateContext) => {
    persistTemplate(next);
  };
  const handleSaveAsNew = () => {
    setSaveAsNewMode(true);
    setCurrentView('save');
  };
  const handleReloadLatest = (_templateId: number) => {
    // The simplest correct UX: navigate to the Templates view so the user
    // explicitly picks "latest" and clicks Edit again. Doing the reload
    // silently would discard any in-progress edits without warning, which
    // is a surprise we don't want — make the discard explicit.
    setCurrentView('open-template');
  };

  // Triggered by ImportExport when a .dpt import resolves to a new-
  // template branch. The server has NOT committed anything; we own the
  // "open in Word + prefill the save form" flow so the user explicitly
  // chooses the DocumentType before clicking Save.
  const handlePendingCreateImport = async (prefill: PendingCreateImport) => {
    try {
      await loadDocumentFromBase64(prefill.fileData);
      // Stamp the source PublicId on the doc so the BindingBanner shows
      // the right state, AND so SaveTemplate can read it back via the
      // already-existing dp_publicId read in its useEffect.
      if (prefill.sourcePublicId) {
        await setDocumentProperty('dp_publicId', prefill.sourcePublicId);
      }
      // Prefill DocumentType too — the user can change it via Placeholders
      // → Document Type before saving. Pre-filling avoids the awkward
      // "blank doc type" save error on the first attempt.
      if (prefill.suggestedDocumentType) {
        await setDocumentProperty('dp_docType', prefill.suggestedDocumentType);
        await setDocumentProperty('dp_docTypeGroup', prefill.suggestedDocumentType);
      }
      await setDocumentProperty('dp_templateName', prefill.suggestedName);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[App] failed to seed Word doc for pending-create import:', err);
      // Fall through and still navigate — the user may be able to recover
      // by reloading the doc manually.
    }
    // No active template yet (will be set after Save). Drop any stale one.
    setActiveTemplate(null);
    setPendingImport(prefill);
    setSaveAsNewMode(true);
    setCurrentView('save');
  };

  const handleSaveClick = () => {
    if (activeTemplate) {
      // Template already saved — background save (no form needed)
      backgroundSave(activeTemplate);
    } else {
      setCurrentView('save');
    }
  };

  return (
    <div className="dp-addin" onClick={() => setDropdownOpen(false)}>
      <div className="dp-addin__header">
        {currentView !== 'placeholders' && currentView !== 'artemis-check' && currentView !== 'artemis-generation' && (
          <>
            <button className="dp-addin__back-btn" onClick={() => setCurrentView('placeholders')} title="Back">&larr;</button>
            <span className="dp-addin__title">{label}</span>
          </>
        )}
        {currentView === 'artemis-check' && (
          <>
            <button className="dp-addin__back-btn" onClick={() => setCurrentView('placeholders')} title="Back">&larr;</button>
            <span className="dp-addin__title">Artemis Check</span>
          </>
        )}
        {currentView === 'artemis-generation' && (
          <>
            <button className="dp-addin__back-btn" onClick={() => setCurrentView('placeholders')} title="Back">&larr;</button>
            <span className="dp-addin__title">Artemis Generation</span>
          </>
        )}
        {currentView === 'placeholders' && (
          <Can anyOf={['master:write', 'template:write']}>
            <div className="dp-addin__new-wrap" onClick={e => e.stopPropagation()}>
              <button
                className={`dp-addin__new-btn${createMode ? ' dp-addin__new-btn--active' : ''}`}
                onClick={() => setDropdownOpen(o => !o)}
              >
                + New <span className="dp-addin__new-caret">&#9662;</span>
              </button>
              {dropdownOpen && (
                <div className="dp-addin__new-dropdown">
                  <Can permission="master:write">
                    <button className="dp-addin__new-option" onClick={() => handleNewOption('keyword')}>
                      <span className="dp-addin__new-option-icon">&#8484;</span> Keyword
                    </button>
                  </Can>
                  <Can permission="template:write">
                    <button className="dp-addin__new-option" onClick={() => handleNewOption('advanced-condition')}>
                      <span className="dp-addin__new-option-icon">&#9881;</span> Advanced Condition
                    </button>
                  </Can>
                  <Can permission="master:write">
                    <button className="dp-addin__new-option" onClick={() => handleNewOption('document-type')}>
                      <span className="dp-addin__new-option-icon">&#8862;</span> Document Type
                    </button>
                  </Can>
                  <Can permission="master:write">
                    <button className="dp-addin__new-option" onClick={() => { setDropdownOpen(false); setCurrentView('fragments'); }}>
                      <span className="dp-addin__new-option-icon">&#9998;</span> Fragments
                    </button>
                  </Can>
                </div>
              )}
            </div>
          </Can>
        )}
        <UserMenu />
      </div>

      {/* Background save/publish status banner */}
      {(savingInBackground || saveMessage) && (
        <div style={{
          background: savingInBackground ? '#eff6ff' : saveMessage?.type === 'success' ? '#f0fdf4' : '#fef2f2',
          borderBottom: `1px solid ${savingInBackground ? '#bfdbfe' : saveMessage?.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          padding: '8px 12px', fontSize: 12, fontWeight: 500,
          color: savingInBackground ? '#1d4ed8' : saveMessage?.type === 'success' ? '#166534' : '#991b1b',
          textAlign: 'center',
        }}>
          {savingInBackground && !saveMessage ? 'Saving template...' : saveMessage?.text}
        </div>
      )}

      {/* Sticky binding-state banner. Always present above the view router
       *  so it can't be missed when the user opens a .docx from desktop.
       *  Renders null in the common "no binding / unbound" case. */}
      <BindingBanner
        activeTemplate={activeTemplate}
        onRebind={handleRebind}
        onSaveAsNew={handleSaveAsNew}
        onReloadLatest={handleReloadLatest}
      />

      <div className="dp-addin__content">
        {currentView === 'placeholders' && (
          <PlaceholderList
            createMode={createMode}
            onCloseCreate={() => setCreateMode(null)}
            activeTemplate={activeTemplate}
            onTemplateNameChange={(name) => persistTemplate(activeTemplate ? { ...activeTemplate, name } : null)}
          />
        )}
        {currentView === 'create-new' && (
          <Can permission="master:write" fallback={<NotAllowed />}><CreatePlaceholder /></Can>
        )}
        {currentView === 'repeating' && (
          <Can permission="template:write" fallback={<NotAllowed />}><RepeatingPlaceholders /></Can>
        )}
        {currentView === 'info' && <TemplateInfo />}
        {currentView === 'open-template' && (
          <OpenTemplate onSaved={handleTemplateLoaded} />
        )}
        {currentView === 'save' && (
          <Can permission="template:write" fallback={<NotAllowed />}>
            <SaveTemplate
              activeTemplate={activeTemplate}
              onSaved={handleTemplateSaved}
              forceSaveAsNew={saveAsNewMode}
              onCancelSaveAsNew={() => { setSaveAsNewMode(false); setPendingImport(null); }}
              pendingImport={pendingImport}
            />
          </Can>
        )}
        {currentView === 'publish' && (
          <Can permission="template:publish" fallback={<NotAllowed />}>
            <PublishTemplate
              activeTemplate={activeTemplate}
              onPublished={(updated) => persistTemplate(updated)}
            />
          </Can>
        )}
        {currentView === 'preview' && <PreviewPanel />}
        {(currentView === 'import' || currentView === 'export') && (
          <Can permission="template:write" fallback={<NotAllowed />}>
            <ImportExport
              onPendingCreateImport={handlePendingCreateImport}
              onImportCommitted={handleTemplateSaved}
            />
          </Can>
        )}
        {currentView === 'fragments' && (
          <Can permission="master:write" fallback={<NotAllowed />}><FragmentPanel /></Can>
        )}
        {currentView === 'artemis-check' && <ArtemisCheck templateId={activeTemplate?.id} />}
        {currentView === 'artemis-generation' && <ArtemisGeneration />}
        {currentView === 'help' && <HelpPanel />}
      </div>
      <ConfirmDialog />
    </div>
  );
}

function NotAllowed() {
  return (
    <div style={{ padding: 24, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
      <div style={{ fontWeight: 600, color: '#334155', marginBottom: 4 }}>Not available</div>
      <div>You don't have permission for this action. Contact your administrator if you need access.</div>
    </div>
  );
}

function UserMenu() {
  const [open, setOpen] = useState(false);
  const user = auth.getUser();
  if (!user) return null;
  const tenantLabel = user.tenantId === 4 ? 'Howden' : user.tenantId === 5 ? 'Applied' : `T${user.tenantId}`;
  const initials = user.email.split('@')[0].slice(0, 2).toUpperCase();
  const handleSignOut = async () => {
    setOpen(false);
    await auth.logout();
  };
  return (
    <div style={{ marginLeft: 'auto', position: 'relative' }} onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen((o) => !o)}
        title={`${user.email} — ${tenantLabel} — ${user.role}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 8px', background: 'transparent', border: '1px solid #cbd5e1',
          borderRadius: 6, cursor: 'pointer', fontSize: 12, color: '#334155',
        }}
      >
        <span style={{
          width: 22, height: 22, borderRadius: '50%', background: '#0d9488',
          color: 'white', fontWeight: 600, fontSize: 11,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{initials}</span>
        <span style={{ fontWeight: 500 }}>{tenantLabel}</span>
        <span style={{ fontSize: 9 }}>&#9662;</span>
      </button>
      {open && (
        <div style={{
          position: 'absolute', right: 0, top: 'calc(100% + 4px)',
          minWidth: 200, background: 'white', border: '1px solid #e2e8f0',
          borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', zIndex: 100,
          fontSize: 12,
        }}>
          <div style={{ padding: '8px 12px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ fontWeight: 600, color: '#0f172a' }}>{user.email}</div>
            <div style={{ color: '#64748b', marginTop: 2 }}>{tenantLabel} — {user.role}</div>
          </div>
          <button
            onClick={handleSignOut}
            style={{
              display: 'block', width: '100%', textAlign: 'left',
              padding: '8px 12px', background: 'transparent', border: 'none',
              cursor: 'pointer', fontSize: 12, color: '#dc2626',
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
